// Frequency-proportional grace window for the on-time-rate metric.
// Returned grace is always within [2, 14] in formula mode. Rule:
// cycleDays = baseDays * (interval ?? 1); grace = clamp(round(cycle/4),
// 2, 14). Monthly's 30/4 = 7.5 boundary is pinned to 7 to honour the
// brief's table. One-off meetings (no rule) fall back to 7.
//
// Per-contract overrides (precedence: per-frequency map > single value
// > formula). The single value can be 0 ("no grace — must hit the
// scheduled date"). The per-frequency map keys are the recurrence
// frequencies + 'oneOff'; an unset key falls through to the next
// precedence level.

import type { RecurrenceRule } from "../../../../types";

const BASE_DAYS: Record<RecurrenceRule["frequency"], number> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  annually: 365,
};

const MIN_GRACE = 2;
const MAX_GRACE = 14;
const ONE_OFF_GRACE = 7;

export type GraceFrequencyKey =
  | RecurrenceRule["frequency"]
  | "oneOff";

export interface MeetingGraceConfig {
  /** Single-mode override applied to every meeting when set. Allows 0. */
  graceDays?: number | null;
  /** Per-frequency override map. Keys override the formula and the
   *  single value for matching frequencies. */
  graceDaysByFrequency?: Partial<Record<GraceFrequencyKey, number>> | null;
}

function frequencyKey(rule?: RecurrenceRule): GraceFrequencyKey {
  if (!rule) return "oneOff";
  return rule.frequency;
}

export function meetingGraceDays(
  rule?: RecurrenceRule,
  override?: number | MeetingGraceConfig | null,
): number {
  // Backwards-compat: accept the legacy plain-number override directly.
  if (typeof override === "number" && Number.isFinite(override)) {
    return override;
  }

  if (override && typeof override === "object") {
    const key = frequencyKey(rule);
    const perFreq = override.graceDaysByFrequency?.[key];
    if (typeof perFreq === "number" && Number.isFinite(perFreq)) {
      return perFreq;
    }
    if (
      typeof override.graceDays === "number" &&
      Number.isFinite(override.graceDays)
    ) {
      return override.graceDays;
    }
  }

  // Formula default.
  if (!rule) return ONE_OFF_GRACE;
  const interval = rule.interval ?? 1;
  const cycleDays = BASE_DAYS[rule.frequency] * interval;
  // Brief §3 table pins monthly to 7 (cycle 30, /4 = 7.5 — round-down).
  if (cycleDays === 30) return 7;
  const computed = Math.round(cycleDays / 4);
  return Math.max(MIN_GRACE, Math.min(computed, MAX_GRACE));
}
